import { useEffect, useState } from "react";

import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSaveInfraSettings } from "@/features/servers/hooks/useInfraSettings";

interface BlacklistEmailEditorProps {
  emails: string[];
}

/**
 * Lista negra de emails placeholder (viewmed_config.black_list_email).
 */
export function BlacklistEmailEditor({ emails }: BlacklistEmailEditorProps) {
  const saveSettings = useSaveInfraSettings();
  const [rows, setRows] = useState<string[]>([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    setRows(emails.length > 0 ? [...emails] : []);
  }, [emails]);

  const handleAdd = () => {
    const email = draft.trim().toLowerCase();
    if (!email) return;
    if (!email.includes("@")) {
      toast.error("Indicá un email válido");
      return;
    }
    if (rows.includes(email)) {
      toast.error("Ese email ya está en la lista");
      return;
    }
    setRows((prev) => [...prev, email]);
    setDraft("");
  };

  const handleSave = async () => {
    try {
      await saveSettings.mutateAsync({ blackListEmail: rows });
      toast.success("Lista negra de emails guardada");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No se pudo guardar la lista"
      );
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">
          Lista negra de emails
        </CardTitle>
        <CardDescription>
          Emails placeholder o inválidos a ignorar (campo{" "}
          <code className="text-xs">black_list_email</code> en viewmed_config).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="flex gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="blacklist-email" className="sr-only">
              Email
            </Label>
            <Input
              id="blacklist-email"
              type="email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="sincorreo@ejemplo.com"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
          </div>
          <Button type="button" variant="outline" onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-1.5" />
            Agregar
          </Button>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Lista vacía.</p>
        ) : (
          <ul className="space-y-1 max-h-64 overflow-y-auto rounded-md border p-2">
            {rows.map((email) => (
              <li
                key={email}
                className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50"
              >
                <span className="font-mono text-xs break-all">{email}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8"
                  aria-label={`Quitar ${email}`}
                  onClick={() =>
                    setRows((prev) => prev.filter((e) => e !== email))
                  }
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">{rows.length} email(s)</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleSave()}
          disabled={saveSettings.isPending}
        >
          {saveSettings.isPending ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          Guardar lista negra
        </Button>
      </CardContent>
    </Card>
  );
}
